import { objectSpread2 as _objectSpread2 } from './_virtual/_rollupPluginBabelHelpers.mjs';
import { parse as parse$1, VALIDATION_SKIPPED, VALIDATION_UNDEFINED, getName } from '@conform-to/dom';
import * as z from 'zod';

function getFieldsetConstraint(source) {
  function inferConstraint(schema) {
    var constraint = {};
    if (schema instanceof z.ZodEffects) {
      constraint = _objectSpread2({}, inferConstraint(schema.innerType()));
    } else if (schema instanceof z.ZodOptional) {
      constraint = _objectSpread2(_objectSpread2({}, inferConstraint(schema.unwrap())), {}, {
        required: false
      });
    } else if (schema instanceof z.ZodDefault) {
      constraint = _objectSpread2(_objectSpread2({}, inferConstraint(schema.removeDefault())), {}, {
        required: false
      });
    } else if (schema instanceof z.ZodArray) {
      constraint = _objectSpread2(_objectSpread2({}, inferConstraint(schema.element)), {}, {
        multiple: true
      });
    } else if (schema instanceof z.ZodString) {
      for (var check of schema._def.checks) {
        switch (check.kind) {
          case 'min':
            if (!constraint.minLength || constraint.minLength < check.value) {
              constraint.minLength = check.value;
            }
            break;
          case 'max':
            if (!constraint.maxLength || constraint.maxLength > check.value) {
              constraint.maxLength = check.value;
            }
            break;
          case 'regex':
            if (!constraint.pattern) {
              constraint.pattern = check.regex.source;
            }
            break;
        }
      }
    } else if (schema instanceof z.ZodNumber) {
      for (var _check of schema._def.checks) {
        switch (_check.kind) {
          case 'min':
            if (!constraint.min || constraint.min < _check.value) {
              constraint.min = _check.value;
            }
            break;
          case 'max':
            if (!constraint.max || constraint.max > _check.value) {
              constraint.max = _check.value;
            }
            break;
        }
      }
    } else if (schema instanceof z.ZodEnum) {
      constraint.pattern = schema.options.map(option =>
      // To escape unsafe characters on regex
      option.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d')).join('|');
    }
    if (typeof constraint.required === 'undefined') {
      constraint.required = true;
    }
    return constraint;
  }
  var keys = ['required', 'minLength', 'maxLength', 'min', 'max', 'step', 'multiple', 'pattern'];
  function resolveFieldsetConstraint(schema) {
    if (schema instanceof z.ZodObject) {
      var result = {};
      for (var [key, def] of Object.entries(schema.shape)) {
        // @ts-expect-error
        result[key] = inferConstraint(def);
      }
      return result;
    }
    if (schema instanceof z.ZodEffects) {
      return resolveFieldsetConstraint(schema.innerType());
    } else if (schema instanceof z.ZodOptional) {
      return resolveFieldsetConstraint(schema.unwrap());
    } else if (schema instanceof z.ZodIntersection) {
      return _objectSpread2(_objectSpread2({}, resolveFieldsetConstraint(schema._def.left)), resolveFieldsetConstraint(schema._def.right));
    } else if (schema instanceof z.ZodUnion || schema instanceof z.ZodDiscriminatedUnion) {
      var options = schema.options;
      return options.map(resolveFieldsetConstraint).reduce((prev, next) => {
        var list = new Set([...Object.keys(prev), ...Object.keys(next)]);
        var result = {};
        for (var name of list) {
          // @ts-expect-error
          var prevConstraint = prev[name];
          // @ts-expect-error
          var nextConstraint = next[name];
          if (prevConstraint && nextConstraint) {
            result[name] = {};
            for (var _key of keys) {
              if (typeof prevConstraint[_key] !== 'undefined' && typeof nextConstraint[_key] !== 'undefined' && prevConstraint[_key] === nextConstraint[_key]) {
                result[name][_key] = prevConstraint[_key];
              }
            }
          } else {
            result[name] = _objectSpread2(_objectSpread2(_objectSpread2({}, prevConstraint), nextConstraint), {}, {
              required: false
            });
          }
        }
        return result;
      });
    }
    return {};
  }
  return resolveFieldsetConstraint(source);
}
function parse(payload, config) {
  return parse$1(payload, {
    resolve(payload, intent) {
      var schema = typeof config.schema === 'function' ? config.schema(intent) : config.schema;
      var resolveResult = result => {
        if (result.success) {
          return {
            value: result.data
          };
        }
        return {
          error: result.error.errors.reduce((result, e) => {
            var _config$acceptMultipl;
            var name = getName(e.path);
            if (typeof result[name] === 'undefined') {
              result[name] = e.message;
            } else if ((_config$acceptMultipl = config.acceptMultipleErrors) !== null && _config$acceptMultipl !== void 0 && _config$acceptMultipl.call(config, {
              name,
              intent,
              payload
            })) {
              result[name] = [].concat(result[name], e.message);
            }
            return result;
          }, {})
        };
      };
      return config.async ? schema.safeParseAsync(payload, {
        errorMap: config.errorMap
      }).then(resolveResult) : resolveResult(schema.safeParse(payload, {
        errorMap: config.errorMap
      }));
    }
  });
}

/**
 * A helper function to define a custom constraint on a superRefine check.
 * Mainly used for async validation.
 *
 * @see https://conform.guide/api/zod#refine
 */
function refine(ctx, options) {
  if (!options.when) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: VALIDATION_SKIPPED,
      path: options.path
    });
    return;
  }

  // Run the validation
  var result = options.validate();
  if (typeof result === 'undefined') {
    // Validate only if the constraint is defined
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: VALIDATION_UNDEFINED,
      path: options.path
    });
    return;
  }
  var reportInvalid = valid => {
    if (valid) {
      return;
    }
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: options.message,
      path: options.path
    });
  };
  return typeof result === 'boolean' ? reportInvalid(result) : result.then(reportInvalid);
}
function ifNonEmptyString(fn) {
  return value => {
    if (typeof value !== 'string') {
      return value;
    }
    if (value === '') {
      return undefined;
    }
    return fn(value);
  };
}

export { getFieldsetConstraint, ifNonEmptyString, parse, refine };
